import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Select } from '../ui/select'
import { Switch } from '../ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Product, ProductCreate, ProductUpdate } from '../../types/product'
import { VAT_RATES, WITHHOLDING_RATES } from '../../types/invoice'

interface ProductFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: ProductCreate | ProductUpdate) => Promise<void>
  product?: Product | null
  isLoading?: boolean
}

export function ProductForm({
  open,
  onClose,
  onSubmit,
  product,
  isLoading = false,
}: ProductFormProps) {
  const { t } = useTranslation()
  const isEditing = !!product

  const [formData, setFormData] = useState<ProductCreate>({
    name: '',
    description: '',
    price: 0,
    vatRate: 21,
    withholdingRate: 0,
    unit: '',
    sku: '',
    isActive: true,
  })

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        description: product.description || '',
        price: product.price,
        vatRate: product.vatRate,
        withholdingRate: product.withholdingRate,
        unit: product.unit || '',
        sku: product.sku || '',
        isActive: product.isActive,
      })
    } else {
      setFormData({
        name: '',
        description: '',
        price: 0,
        vatRate: 21,
        withholdingRate: 0,
        unit: '',
        sku: '',
        isActive: true,
      })
    }
  }, [product, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(formData)
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing
                ? t('settings.products.edit')
                : t('settings.products.add')}
            </DialogTitle>
            <DialogDescription>
              {t('settings.products.formDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t('settings.products.name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('settings.products.namePlaceholder')}
                required
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">{t('settings.products.description')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('settings.products.descriptionPlaceholder')}
                rows={2}
                disabled={isLoading}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="price">{t('settings.products.price')}</Label>
                <Input
                  id="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })
                  }
                  placeholder="0.00"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="unit">{t('settings.products.unit')}</Label>
                <Input
                  id="unit"
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  placeholder={t('settings.products.unitPlaceholder')}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="vatRate">{t('settings.products.vatRate')}</Label>
                <Select
                  id="vatRate"
                  value={String(formData.vatRate)}
                  onChange={(e) =>
                    setFormData({ ...formData, vatRate: parseInt(e.target.value) })
                  }
                  disabled={isLoading}
                >
                  {VAT_RATES.map((rate) => (
                    <option key={rate.value} value={rate.value}>
                      {rate.label}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="withholdingRate">
                  {t('settings.products.withholdingRate')}
                </Label>
                <Select
                  id="withholdingRate"
                  value={String(formData.withholdingRate)}
                  onChange={(e) =>
                    setFormData({ ...formData, withholdingRate: parseInt(e.target.value) })
                  }
                  disabled={isLoading}
                >
                  {WITHHOLDING_RATES.map((rate) => (
                    <option key={rate.value} value={rate.value}>
                      {rate.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="sku">{t('settings.products.sku')}</Label>
              <Input
                id="sku"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                placeholder={t('settings.products.skuPlaceholder')}
                disabled={isLoading}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isActive: checked })
                }
                disabled={isLoading}
              />
              <Label htmlFor="isActive">
                {t('settings.products.isActive')}
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saving')}
                </>
              ) : (
                t('common.save')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
